import { makeAutoObservable, runInAction } from "mobx";
import type { InfiniteData } from "@tanstack/react-query";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import { invalidateScoped, patchScoped } from "@/queries/invalidation";
import { workspaceInvitationsKey } from "@/queries/invitations";
import {
  findCachedMember,
  isMemberBankKey,
  isMembersPageKey,
  memberEligibleRolesKey,
  type MembersPageQuery,
} from "@/queries/members";
import { scopedKey } from "@/queries/scope";
import { invalidateTeamRosters, teamIdOfRosterKey } from "@/queries/teams";
import type { IWorkspaceMembersPage } from "@/services/workspace.service";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type {
  IWorkspaceBulkInviteFormData,
  IWorkspaceMember,
  IWorkspaceMemberInvitation,
} from "@/types/workspace";
import type { TUserImportRequest, TUserImportResult } from "@/types/user-import";

/**
 * The workspace members' and invitations' writes. Member data is read only
 * through Query (NG-2797): the bank (R20) through `useMemberBank` /
 * `readMemberBank`, the members table (R21) through `useMembersPage`, role
 * eligibility (R22), profile (R23) and work hours (R24) through their hooks.
 * So are a workspace's invitations, through `useWorkspaceInvitations`
 * (NG-2814). The writes here patch or invalidate those cached entries, so
 * every reader on screen shows a confirmed write.
 */
export class WorkspaceMembersStore {
  isInviting = false;
  activeMutationMemberId: string | null = null;
  activeInvitationId: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The scope `slug`'s cached member reads live under, or `null` before the session resolves. */
  private scope(slug: string) {
    return queryCacheScope(slug, "workspace-members");
  }

  /**
   * A role change can shift which roles are (in)eligible: reset that member's
   * R22 entry by its full key (`('member', mid)` is also the prefix of the
   * member's team list, R19, which a role change does not touch). Reset rather
   * than removed: a picker observing it would keep a removed entry, and a
   * check in flight from before the change could still land on it. Resetting
   * cancels that check, clears the answer and re-reads for an open picker.
   */
  private forgetEligibility(slug: string, memberId: string): void {
    const scope = this.scope(slug);
    if (!scope) return;
    peekQueryClient()
      ?.resetQueries({ queryKey: memberEligibleRolesKey(scope, memberId), exact: true })
      .catch((error: unknown) => {
        console.warn("[workspace-members] resetting a member's role eligibility failed", {
          slug,
          memberId,
          error,
        });
      });
  }

  /** Apply a confirmed change to one member row in every cached bank and page. */
  patchMember(memberId: string, patch: Partial<IWorkspaceMember>): void {
    const apply = (m: IWorkspaceMember) => (m.id === memberId ? { ...m, ...patch } : m);
    this.patchCachedBanks((members) => members.map(apply));
    this.patchCachedPages((page) => ({ ...page, results: page.results.map(apply) }));
  }

  /** Replace one member row, as the server returned it, in every cached bank and page. */
  replaceMember(member: IWorkspaceMember): void {
    const apply = (existing: IWorkspaceMember) => (existing.id === member.id ? member : existing);
    this.patchCachedBanks((members) => members.map(apply));
    this.patchCachedPages((page) => ({ ...page, results: page.results.map(apply) }));
  }

  /**
   * A user's own profile change (their avatar, read with the session) shown on
   * every cached member row for that user: every bank (R20) and page (R21), so
   * the pickers and the members table show it. Writes nothing when no row
   * needs the change, since the session is read again on most navigations.
   */
  patchMemberUser(userId: string, patch: Partial<IWorkspaceMember["member"]>): void {
    const needs = (member: IWorkspaceMember) =>
      member.member.id === userId &&
      Object.entries(patch).some(
        ([field, value]) => member.member[field as keyof IWorkspaceMember["member"]] !== value,
      );
    const apply = (member: IWorkspaceMember) =>
      needs(member) ? { ...member, member: { ...member.member, ...patch } } : member;
    this.patchCachedBanks((members) => (members.some(needs) ? members.map(apply) : members));
    this.patchCachedPages((page) =>
      page.results.some(needs) ? { ...page, results: page.results.map(apply) } : page,
    );
  }

  /**
   * Apply a confirmed member write to every cached bank (R20) that holds it,
   * so a fresh entry can never show the value the write replaced. A member id
   * is a workspace binding, unique across workspaces, so the match needs no
   * slug. With no client, or no entry, there is nothing to patch.
   */
  private patchCachedBanks(update: (members: IWorkspaceMember[]) => IWorkspaceMember[]): void {
    peekQueryClient()?.setQueriesData<IWorkspaceMember[]>(
      { predicate: ({ queryKey }) => isMemberBankKey(queryKey) },
      (current) => (current === undefined ? undefined : update(current)),
    );
  }

  /** Apply a confirmed member write to every cached members page (R21), as for the bank. */
  private patchCachedPages(update: (page: IWorkspaceMembersPage) => IWorkspaceMembersPage): void {
    peekQueryClient()?.setQueriesData<InfiniteData<IWorkspaceMembersPage>>(
      { predicate: ({ queryKey }) => isMembersPageKey(queryKey) },
      (current) =>
        current === undefined ? undefined : { ...current, pages: current.pages.map(update) },
    );
  }

  /**
   * Mark `slug`'s cached member bank (R20) and every members page (R21) stale
   * and re-read the ones on screen: for a write that changed member rows in a
   * way this store cannot patch.
   */
  invalidateCachedMembers(slug: string): Promise<void> {
    const scope = this.scope(slug);
    if (!scope) return Promise.resolve();
    return invalidateScoped(getQueryClient(), scope, "members");
  }

  /**
   * Mark the team rosters a member change reaches stale (contract §3.3): the
   * team lists that embed rosters (R14 with members), every cached roster of
   * the workspace (R17), and each named member's team list (R19). Which teams
   * a member was on is not on the member row (it carries team names only), so
   * every cached roster is included rather than a guessed subset.
   */
  private invalidateTeamRostersFor(slug: string, memberIds: readonly string[]): Promise<void> {
    const scope = this.scope(slug);
    if (!scope) return Promise.resolve();
    const client = getQueryClient();
    const teamIds = client
      .getQueryCache()
      .findAll({ queryKey: scopedKey(scope, "team") })
      .map(({ queryKey }) => teamIdOfRosterKey(queryKey))
      .filter((teamId): teamId is string => teamId !== null);
    return invalidateTeamRosters(client, scope, { teamIds, memberIds });
  }

  /**
   * Cancel the members page reads in flight (#2368): a read that started
   * before a write would land the page it was sent for over the write. Query
   * reverts each entry to what it held, including any patch made since, so
   * nothing older is cached.
   *
   * An entry whose FIRST read was cancelled reverts to nothing: no data and no
   * read in flight. A table on that key (a search typed just before the write)
   * would then show its previous key's placeholder rows for good, without the
   * write. So those entries read again, after the write.
   */
  private cancelPageReads(): void {
    const client = peekQueryClient();
    if (!client) return;
    const isPage = ({ queryKey }: { queryKey: readonly unknown[] }) => isMembersPageKey(queryKey);
    client
      .cancelQueries({ predicate: isPage })
      .then(() =>
        client.invalidateQueries({
          predicate: (query) => isPage(query) && query.state.data === undefined,
        }),
      )
      .catch((error: unknown) => {
        console.warn("[workspace-members] cancelling members page reads failed", error);
      });
  }

  /** Export the members list as CSV, scoped to the table's query and (when
   *  `ids` is non-empty) restricted to the selected rows. The query goes
   *  through whole, so every filter the table applies reaches the export. */
  exportMembersCsv(
    slug: string,
    query: MembersPageQuery,
    ids?: string[],
    columns?: string[],
  ): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.workspaceService.exportMembers(slug, body, query);
  }

  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.workspaceService.getExportStatus(slug, tok),
    );
  }

  /** Apply a confirmed invitation write to `slug`'s cached invitations, if a reader loaded them. */
  private patchInvitations(
    slug: string,
    update: (invitations: IWorkspaceMemberInvitation[]) => IWorkspaceMemberInvitation[],
  ): void {
    const scope = this.scope(slug);
    const client = peekQueryClient();
    if (!scope || !client) return;
    patchScoped<IWorkspaceMemberInvitation[]>(client, workspaceInvitationsKey(scope), update);
  }

  /** Replace one invitation, as the server returned it, in `slug`'s cached invitations. */
  private replaceInvitation(slug: string, updated: IWorkspaceMemberInvitation): void {
    this.patchInvitations(slug, (invitations) =>
      invitations.map((invitation) => (invitation.id === updated.id ? updated : invitation)),
    );
  }

  async inviteMembers(
    slug: string,
    data: IWorkspaceBulkInviteFormData,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceMemberInvitation[]> {
    this.isInviting = true;
    try {
      const invitations = await this.rootStore.workspaceService.inviteMembers(slug, data);
      // Before the refetch, so a failed reload doesn't drop it (NG-2063).
      track("workspace_member_invited", { count: invitations.length, source });
      // The answer is the new rows, not the list: re-read the list. A failed
      // re-read is logged by the cache and leaves the tab on its last answer;
      // the invite still went out, so it never fails the dialog.
      const scope = this.scope(slug);
      if (scope) await invalidateScoped(getQueryClient(), scope, "invitations", "list");
      return invitations;
    } finally {
      runInAction(() => {
        this.isInviting = false;
      });
    }
  }

  /**
   * Bulk-import members from parsed CSV rows (NG-1307, epic NG-1288).
   *
   * Unlike {@link inviteMembers} this creates the accounts outright, so the
   * people it creates are already in the members list rather than in the
   * invitations. That is why a successful import re-reads the cached members
   * rather than the invitations: a `dry_run` writes nothing, so it
   * deliberately reloads nothing either.
   *
   * **`send_welcome_email` does not change that** (NG-1304). It mails everybody
   * the import brought in, but the email is a "you have been added" notice and
   * writes no invitation — deliberately, because a pending invite for somebody
   * already a member would sit in that panel for good and would make the *next*
   * genuine invitation to them a silent no-op. So the invitations are untouched
   * whichever way the switch is set, and nothing here needs to refetch it.
   */
  async bulkImportUsers(slug: string, body: TUserImportRequest): Promise<TUserImportResult> {
    const report = await this.rootStore.workspaceService.bulkImportUsers(slug, body);
    // Updated rows count as much as created ones (NG-1297): a file that only
    // corrects people already in the list changes names and roles the table is
    // showing right now, so reloading on ``created_count`` alone left the admin
    // looking at the values they had just replaced.
    if (!report.dry_run && report.created_count + report.updated_count > 0) {
      // Best-effort, and deliberately not allowed to reject: the import has
      // already happened, so letting a failed *reload* propagate would have the
      // dialog report a successful import as a failure and invite the admin to
      // run it again. The stale list costs them a refresh; a second import would
      // not. Nothing is hidden by this: the members table shows its own read's
      // error state.
      try {
        // The bank and the pages may still be fresh in the cache, and they
        // predate the import: re-read them.
        await this.invalidateCachedMembers(slug);
        // People it placed on teams are now on those teams' rosters.
        this.invalidateTeamRostersFor(slug, []).catch((error: unknown) => {
          console.warn(
            `[workspace-members] marking team rosters stale after import failed for workspace ${slug}`,
            error,
          );
        });
      } catch (err) {
        console.warn(
          `[workspace-members] members list reload after import failed for workspace ${slug}; the import itself succeeded`,
          err,
        );
      }
    }
    // Not on a dry run. The preview (NG-1308) goes through this same method, so
    // an unguarded `track` counts every import twice — once when the admin is
    // still deciding — and counts an import they looked at and abandoned as one
    // that happened. The reload above is already guarded for the same reason.
    if (!report.dry_run) {
      track("workspace_members_bulk_imported", {
        created: report.created_count,
        updated: report.updated_count,
        skipped: report.skipped_count,
        errors: report.error_count,
      });
    }
    return report;
  }

  async updateMember(
    slug: string,
    memberId: string,
    data: Partial<IWorkspaceMember>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspaceMember> {
    this.activeMutationMemberId = memberId;
    // Read before the write: `fields` is what this save changed (NG-2063). A
    // role is named only as a changed field, since a custom role's slug can
    // carry its name.
    const before = findCachedMember((m) => m.id === memberId);
    const fields = changedFields(data, before);
    try {
      const updated = await this.rootStore.workspaceService.updateMember(slug, memberId, data);
      // Cancel any page read already in flight so its (pre-write) answer can't
      // land afterwards and revert this echoed update: the cell shows the new
      // value without a reload (#2368).
      this.cancelPageReads();
      // By the id written to, not the answer's: a role change re-keys the
      // membership row (NG-1236), so the answer can carry a new id.
      const apply = (m: IWorkspaceMember) => (m.id === memberId ? updated : m);
      this.patchCachedBanks((members) => members.map(apply));
      this.patchCachedPages((page) => ({ ...page, results: page.results.map(apply) }));
      // A role change can shift which roles are (in)eligible: the next picker
      // open asks again.
      if (data.role !== undefined) this.forgetEligibility(slug, memberId);
      track("workspace_member_updated", { member_id: memberId, fields, source });
      return updated;
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async removeMember(
    slug: string,
    memberId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeMutationMemberId = memberId;
    try {
      await this.rootStore.workspaceService.deleteMember(slug, memberId);
      track("workspace_member_removed", { member_id: memberId, source });
      // Team rosters still list the removed person until they read again.
      this.invalidateTeamRostersFor(slug, [memberId]).catch((error: unknown) => {
        console.warn(
          `[workspace-members] marking team rosters stale after removing ${memberId} failed`,
          { slug },
          error,
        );
      });
      this.patchCachedBanks((members) => members.filter((member) => member.id !== memberId));
      this.patchCachedPages((page) => {
        const results = page.results.filter((member) => member.id !== memberId);
        return results.length === page.results.length
          ? page
          : { ...page, results, total_count: Math.max(0, page.total_count - 1) };
      });
    } finally {
      runInAction(() => {
        this.activeMutationMemberId = null;
      });
    }
  }

  async revokeInvitation(
    slug: string,
    invitationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      await this.rootStore.workspaceService.deleteInvitation(slug, invitationId);
      track("workspace_invitation_revoked", { invitation_id: invitationId, source });
      this.patchInvitations(slug, (invitations) =>
        invitations.filter((invitation) => invitation.id !== invitationId),
      );
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }

  /** Re-send a pending invite's email and restart its expiry clock (NG-2490). */
  async resendInvitation(
    slug: string,
    invitationId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      const updated = await this.rootStore.workspaceService.resendInvitation(slug, invitationId);
      track("workspace_invitation_resent", { invitation_id: invitationId, source });
      this.replaceInvitation(slug, updated);
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }

  /** Edit a pending invite's role and/or teams (NG-2490). `team_ids` is a
   *  full-set replace, mirroring the invite dialog's own team handling. */
  async patchInvitation(
    slug: string,
    invitationId: string,
    data: { role?: string; team_ids?: string[] },
  ): Promise<void> {
    this.activeInvitationId = invitationId;
    try {
      const updated = await this.rootStore.workspaceService.updateInvitation(
        slug,
        invitationId,
        data,
      );
      this.replaceInvitation(slug, updated);
    } finally {
      runInAction(() => {
        this.activeInvitationId = null;
      });
    }
  }
}
